import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useForm, Controller } from "react-hook-form";
import { useEffect } from "react";

type Props = {
  onClose: () => void;
  open: boolean;
  userID: number;
  onPasswordChanged?: (newPassword: string) => void;
};

type FormValues = {
  newPassword: string;
  confirmPassword: string;
};

const ChangePasswordDialog = (props: Props) => {
  const { control, handleSubmit, watch, reset, formState: { errors }, setError } = useForm<FormValues>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    }
  });

  const newPassword = watch('newPassword');

  useEffect(() => {
    if (props.open) {
      reset();
    }
  }, [props.open, reset]);

  const onSubmit = (data: FormValues) => {
    if (data.newPassword !== data.confirmPassword) {
      setError('confirmPassword', {
        type: 'manual',
        message: 'Lösenorden matchar inte'
      });
      return;
    }

    if (data.newPassword.length < 8) {
      setError('newPassword', {
        type: 'manual',
        message: 'Lösenordet måste vara minst 8 tecken'
      });
      return;
    }

    console.log('Password change submitted:', { userID: props.userID, ...data });

    props.onPasswordChanged?.(newPassword);
    props.onClose();
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                shape="circular"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={props.onClose}
              />
            }
          >
            Ändra lösenord
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-3">
              <Controller
                name="newPassword"
                control={control}
                rules={{
                  required: 'Nytt lösenord är obligatoriskt',
                  minLength: {
                    value: 8,
                    message: 'Lösenordet måste vara minst 8 tecken'
                  }
                }}
                render={({ field }) => (
                  <Field
                    label="Nytt lösenord:"
                    required
                    validationMessage={errors.newPassword?.message}
                    validationState={errors.newPassword ? "error" : "none"}
                  >
                    <Input
                      type="password"
                      value={field.value}
                      onChange={(e, data) => field.onChange(data.value)}
                    />
                  </Field>
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: 'Bekräfta lösenord är obligatoriskt',
                  validate: (value) => value === newPassword || 'Lösenorden matchar inte'
                }}
                render={({ field }) => (
                  <Field
                    label="Bekräfta nytt lösenord:"
                    required
                    validationMessage={errors.confirmPassword?.message}
                    validationState={errors.confirmPassword ? "error" : "none"}
                  >
                    <Input
                      type="password"
                      value={field.value}
                      onChange={(e, data) => field.onChange(data.value)}
                    />
                  </Field>
                )}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Avbryt</Button>
            <Button appearance="primary" onClick={handleSubmit(onSubmit)}>Spara</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ChangePasswordDialog;
